import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ArrowRight,
  BadgePercent,
  Building2,
  Clock,
  LifeBuoy,
  MapPin,
  type LucideIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import TextSubmissionModal from './components/TextSubmissionModal';
import EnterpriseAccountModal from './components/EnterpriseAccountModal';
import {
  useSubmitContactRequestMutation,
  useSubmitSalesRequestMutation,
} from './services/helpSupportApi';
import type { ContactAction, ContactRequest } from '../../types/help-support.types';

type ActiveModal = 'question' | 'enterprise' | 'sales' | null;

interface ContactCard {
  icon: LucideIcon;
  title: string;
  description: string;
  linkText: string;
  onClick?: () => void;
  href?: string;
}

const ContactPage = () => {
  const { search } = useLocation();
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [submitContact] = useSubmitContactRequestMutation();
  const [submitSales] = useSubmitSalesRequestMutation();

  useEffect(() => {
    const params = new URLSearchParams(search);
    if (params.get('openSupport') === 'true') {
      setActiveModal('sales');
    }
    document.title = 'PlanOmatic - Contact Us';
    return () => {
      document.title = 'PlanOmatic';
    };
  }, [search]);

  const handleTextSubmit = async (action: ContactAction, content: string) => {
    const body: ContactRequest = {
      action,
      content,
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      companyName: '',
      doorsUnderManagement: '',
    };
    if (action === 'Sales Submission') {
      await submitSales(body).unwrap();
    } else {
      await submitContact(body).unwrap();
    }
  };

  const cards: ContactCard[] = [
    {
      icon: LifeBuoy,
      title: 'Customer Support',
      description:
        'Get help with an existing order, appointment, or account setup and integration. You can also text us at (720) 637-3225.',
      linkText: 'Submit a Ticket',
      onClick: () => setActiveModal('question'),
    },
    {
      icon: Building2,
      title: 'Enterprise Inquiries',
      description:
        'Do you manage over 1,000 rentals? Learn more about our Enterprise pricing.',
      linkText: 'Contact Sales',
      onClick: () => setActiveModal('enterprise'),
    },
    {
      icon: BadgePercent,
      title: 'Membership Program',
      description:
        'Get 10% off all services for just $20/month. Enjoy 30% off for your first 30 days!',
      linkText: 'Sign Up Now',
      href: 'https://www.planomatic.com/membership',
    },
  ];

  return (
    <section aria-labelledby="contact-page-heading">
      <div className="px-5 py-4 border-b border-border">
        <Typography as="h1" variant="heading-xl" id="contact-page-heading">
          Contact Us
        </Typography>
      </div>

      <div className="px-5 pt-6 grid grid-cols-1 gap-y-5 md:grid-cols-3 md:gap-x-5 md:gap-y-4 md:grid-rows-[auto_auto_1fr_auto]">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.title}
              className="border border-border rounded-lg p-6 bg-card flex flex-col gap-4 md:grid md:grid-rows-subgrid md:row-span-4"
            >
              <div
                className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary md:justify-self-start"
                aria-hidden
              >
                <Icon className="h-5 w-5" />
              </div>
              <Typography as="h2" className="text-lg font-semibold">
                {card.title}
              </Typography>
              <Typography as="p" variant="body" className="text-muted-foreground">
                {card.description}
              </Typography>
              <div>
                {/* These card CTAs are standalone actions (each on its own
                    row), not inline prose links, so they carry the
                    max-lg:min-h-11 tap-target floor themselves — the link
                    variant deliberately omits it for the inline case (WCAG
                    2.5.8 inline exception; see button.tsx). */}
                {card.href ? (
                  // asChild renders the Button's classes (incl. the
                  // max-lg:min-h-11 tap-target floor) directly onto the <a>,
                  // instead of nesting a real <button> inside a real <a>
                  // (invalid HTML whose outer anchor never inherited the
                  // floor -- the real offender behind the old #1101-era
                  // tap-target fixme).
                  <Button variant="link" className="max-lg:min-h-11" asChild>
                    <a href={card.href} target="_blank" rel="noopener noreferrer">
                      {card.linkText}
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </a>
                  </Button>
                ) : (
                  <Button variant="link" className="max-lg:min-h-11" onClick={card.onClick}>
                    {card.linkText}
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="px-5 pt-5 pb-6">
        <div className="border border-border rounded-lg p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-center gap-3">
            <div
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"
              aria-hidden
            >
              <MapPin className="h-5 w-5" />
            </div>
            <div>
              <Typography as="h2" variant="body-semibold">
                World Headquarters
              </Typography>
              <Typography as="p" variant="body" className="text-muted-foreground mt-1">
                2911 Walnut Street
              </Typography>
              <Typography as="p" variant="body" className="text-muted-foreground">
                Denver, CO 80205, USA
              </Typography>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"
              aria-hidden
            >
              <Clock className="h-5 w-5" />
            </div>
            <div>
              <Typography as="h2" variant="body-semibold">
                Hours
              </Typography>
              <Typography as="p" variant="body" className="text-muted-foreground mt-1">
                7 days a week, 8 AM – 8 PM ET
              </Typography>
            </div>
          </div>
        </div>
      </div>

      {activeModal === 'question' && (
        <TextSubmissionModal
          title="How Can We Help?"
          placeholder="I need help with order #0009876 for 123 Main St..."
          action="Question"
          onClose={() => setActiveModal(null)}
          onSubmit={handleTextSubmit}
        />
      )}
      {activeModal === 'sales' && (
        <TextSubmissionModal
          title="Contact Our Sales Team"
          placeholder="I want to learn more about..."
          action="Sales Submission"
          onClose={() => setActiveModal(null)}
          onSubmit={handleTextSubmit}
        />
      )}
      {activeModal === 'enterprise' && (
        <EnterpriseAccountModal onClose={() => setActiveModal(null)} />
      )}
    </section>
  );
};

export default ContactPage;
